import { Children, isValidElement } from 'react';
import { Flex, Heading as RadixHeading, Text } from '@radix-ui/themes';
import Breadcrumbs from '@/components/Breadcrumbs';
import cn from '@/lib/cn';
import logoUrl from '@/images/lawfi-logo.svg';

const Container = ({ children, className, ...props }) => {
  const hasHeading = Children.toArray(children).some((child) => {
    return (
      isValidElement(child) &&
      (child.type === Heading || child.type === HeadingContainer)
    );
  });

  return (
    <Flex
      direction="column"
      className={cn('mx-auto max-w-8xl sm:px-6 lg:px-8', className)}
      mt={hasHeading ? null : '5'}
      {...props}
    >
      {children}
    </Flex>
  );
};

const HeadingContainer = ({ children, gap = '5', ...props }) => (
  <Flex
    direction="column"
    gap={gap}
    className="my-5 mx-5 sm:mx-0 sm:my-8"
    {...props}
  >
    {children}
  </Flex>
);

const Heading = ({ children, ...props }) => (
  <HeadingContainer>
    <RadixHeading {...props}>{children}</RadixHeading>
  </HeadingContainer>
);

const Content = ({ children, gap = '7', className, ...props }) => (
  <Flex
    direction="column"
    gap={gap}
    className={cn(
      'px-5 py-7 sm:p-10 bg-white shadow-sm sm:rounded-lg grow h-min',
      className,
    )}
    {...props}
  >
    {children}
  </Flex>
);

const ContentHeading = ({ description, children }) => (
  <Flex direction="column" gap="1">
    <RadixHeading as="h2" size="5">
      {children}
    </RadixHeading>
    {description && <Text color="gray">{description}</Text>}
  </Flex>
);

const LandingContainer = ({ children }) => {
  return (
    <div className="h-full bg-gray-50">
      <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
        {children}
      </div>
    </div>
  );
};

const LandingHeader = ({ children }) => {
  return (
    <div className="sm:mx-auto sm:w-full sm:max-w-md">
      <img alt="LawFi" src={logoUrl} className="mx-auto h-[55px] w-auto mb-8" />
      <h1 className="mt-6 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
        {children}
      </h1>
    </div>
  );
};

const LandingContent = ({ children }) => {
  return (
    <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
      <div className="bg-white px-6 py-12 shadow-sm sm:rounded-lg sm:px-12">
        {children}
      </div>
    </div>
  );
};

const LandingFooter = ({ children }) => {
  return (
    <Flex
      gap="4"
      justify="center"
      align="center"
      className="mt-10 text-sm text-gray-500"
    >
      {children}
    </Flex>
  );
};

const Page = {
  Container,
  HeadingContainer,
  Breadcrumbs,
  Heading,
  Content,
  ContentHeading,
  LandingContainer,
  LandingHeader,
  LandingContent,
  LandingFooter,
};

export default Page;
